import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import type { StepImageBinding } from "../../utils/ragChatUtils";
import {
  composeSentencePreview,
  getPreviewSentences,
  measureVisibleSentenceCount,
  type StepVideoBinding,
} from "../../utils/ragChatUtils";
import { StepImageCollage } from "./StepImageCollage";
import { StepInlineVideos } from "./StepInlineVideos";
import { RagMarkdownContent } from "./RagMarkdownContent";
import {
  STEP_BADGE_PILL_SX,
  ANSWER_BODY_SX,
  ANSWER_HEADING_SX,
} from "../../constants/ragUiConstants";

/** Card padding — theme spacing 1.75 → 14px. */
const STEP_CARD_PADDING_PX = 14;

/** Description preview — ~3 lines at 14px × 1.5 line-height (21px per line). */
const STEP_DESCRIPTION_MAX_HEIGHT_PX = 63;

/** Body copy on the card (titles use {@link ANSWER_HEADING_SX} when they are labels). */
const STEP_CONTENT_TYPOGRAPHY = ANSWER_BODY_SX;

export interface StepCardProps {
  title: string;
  /** Full step body (video refs already stripped for display). */
  body: string;
  videos?: StepVideoBinding[];
  images?: StepImageBinding[];
  actionLink?: { label: string; url: string };
  onOpenImage?: (src: string) => void;
  stepLabel: string;
  tapToOpenLabel: string;
  morePhotosLabel: (count: number) => string;
  zoomLabel: string;
}

/** True when content should keep line-based preview units (bullets / nested lines). */
function hasIntentionalMultiLineStructure(text: string): boolean {
  return /\n/.test(text) || /^[-*•]\s+/m.test(text);
}

/**
 * True when `title` is a short label (`Check Engine Light`, `Visual Inspection`)
 * that should render as a heading. First-sentence titles that already end in
 * `.!?` stay in the body so period-splits do not become fake headings.
 */
export function isStepCardLabelTitle(title: string, body: string): boolean {
  const t = String(title ?? "").trim();
  const b = String(body ?? "").trim();
  if (!t || !b) return false;
  if (/[.!?]$/.test(t)) return false;
  const words = t.split(/\s+/).filter(Boolean);
  if (words.length < 1 || words.length > 8) return false;
  return t.length <= 80;
}

/** Join parser title/body into one content string for uniform rendering. */
export function joinStepCardContent(title: string, body: string): string {
  const t = String(title ?? "").trim();
  const b = String(body ?? "").trim();
  if (t && b) {
    // Keep newlines only for intentional multi-line / bullet bodies.
    // Flowing prose (e.g. splitStepTitleBody period split) joins with a space so
    // getPreviewSentences does not invent separate line-based preview units.
    if (hasIntentionalMultiLineStructure(b) || hasIntentionalMultiLineStructure(t)) {
      return `${t}\n${b}`;
    }
    return `${t} ${b}`;
  }
  return t || b;
}

function StepDescriptionDialog({
  open,
  onClose,
  stepLabel,
  heading,
  content,
  videos,
  closeLabel,
}: {
  open: boolean;
  onClose: () => void;
  stepLabel: string;
  heading: string;
  content: string;
  videos: StepVideoBinding[];
  closeLabel: string;
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: isMobile ? "16px 16px 0 0" : 2,
          m: isMobile ? 0 : 2,
          ...(isMobile
            ? {
                position: "fixed",
                bottom: 0,
                left: 0,
                right: 0,
                maxHeight: "85vh",
              }
            : {}),
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography
          component="span"
          sx={{
            display: "block",
            color: "primary.main",
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: 0.04,
            textTransform: "uppercase",
          }}
        >
          {stepLabel}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {heading ? (
          <Typography
            component="h3"
            sx={{ ...ANSWER_HEADING_SX, mb: content ? 1 : 0 }}
          >
            {heading}
          </Typography>
        ) : null}
        {content ? (
          <Box
            sx={{
              ...STEP_CONTENT_TYPOGRAPHY,
              "& p, & div": { m: 0, mb: 1, color: "inherit", fontWeight: "inherit" },
              "& p:last-child, & div:last-child": { mb: 0 },
              "& ul": { my: 0.75, pl: 2.5 },
              "& li": { mb: 0.75, wordBreak: "break-word" },
              "& strong, & b": { fontWeight: 400 },
            }}
          >
            <RagMarkdownContent text={content} dense />
          </Box>
        ) : null}
        <StepInlineVideos videos={videos} />
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button onClick={onClose} variant="contained" disableElevation>
          {closeLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function useMeasuredSentencePreview(content: string) {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const sentences = useMemo(() => getPreviewSentences(content), [content]);
  // Bullet / intentional multi-line units keep atomic line fitting; flowing prose
  // puts all text in the 63px box and lets CSS overflow clip wrapped lines.
  const useLineBasedUnits = useMemo(
    () => sentences.some((u) => /^[-*•]/.test(u.trim())),
    [sentences]
  );
  const [visibleCount, setVisibleCount] = useState(sentences.length);
  const [heightOverflow, setHeightOverflow] = useState(false);

  // Flowing prose always renders the full preview string; only clip via CSS.
  const effectiveVisibleCount = useLineBasedUnits ? visibleCount : sentences.length;
  const { preview, isTruncated } = composeSentencePreview(
    sentences,
    effectiveVisibleCount
  );
  const previewMarkdown = preview.replace(/\n?\.\.\.\s*$/, "").trim();

  const remeasure = useCallback(() => {
    const container = containerRef.current;
    const measureEl = measureRef.current;
    if (!container || !measureEl) return;

    Object.assign(measureEl.style, {
      fontSize: `${STEP_CONTENT_TYPOGRAPHY.fontSize}px`,
      lineHeight: String(STEP_CONTENT_TYPOGRAPHY.lineHeight),
      fontWeight: String(STEP_CONTENT_TYPOGRAPHY.fontWeight),
      // Match preview rendering so nested bullet lines measure correctly.
      whiteSpace: "pre-wrap",
      wordWrap: "break-word",
    });
    measureEl.style.width = `${container.clientWidth}px`;

    if (useLineBasedUnits) {
      const measured = measureVisibleSentenceCount(
        sentences,
        measureEl,
        STEP_DESCRIPTION_MAX_HEIGHT_PX,
        container.clientWidth
      );
      // Never show an empty preview when content exists; flag height overflow for Show more.
      const fit = measured > 0 || !sentences.length ? measured : 1;
      setVisibleCount(fit);
      setHeightOverflow(measured === 0 && sentences.length > 0);
      return;
    }

    // Flowing prose: do not inflate container scrollHeight with the measure node.
    measureEl.textContent = "";
    // Show more only when the actual rendered description is clipped (1px tolerance).
    const overflows =
      sentences.length > 0 &&
      container.scrollHeight > container.clientHeight + 1;
    setVisibleCount(sentences.length);
    setHeightOverflow(overflows);
  }, [sentences, useLineBasedUnits]);

  useLayoutEffect(() => {
    remeasure();
  }, [remeasure, previewMarkdown]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;
    const observer = new ResizeObserver(() => remeasure());
    observer.observe(container);
    return () => observer.disconnect();
  }, [remeasure]);

  const shouldShowMore = useLineBasedUnits
    ? isTruncated || heightOverflow
    : heightOverflow;

  return { containerRef, measureRef, preview, previewMarkdown, shouldShowMore };
}

export function StepCard({
  title,
  body,
  videos = [],
  images = [],
  actionLink,
  onOpenImage,
  stepLabel,
  tapToOpenLabel,
  morePhotosLabel,
  zoomLabel,
}: StepCardProps) {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const heading = isStepCardLabelTitle(title, body) ? title.trim() : "";
  const content = useMemo(
    () => (heading ? String(body ?? "").trim() : joinStepCardContent(title, body)),
    [heading, title, body]
  );
  const { containerRef, measureRef, preview, previewMarkdown, shouldShowMore } =
    useMeasuredSentencePreview(content);
  const hasImages = images.length > 0 && !!onOpenImage;
  const hasDescription = Boolean(preview);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          height: "100%",
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          overflow: "hidden",
          boxShadow: "0 1px 3px rgba(28,46,54,0.06)",
          p: `${STEP_CARD_PADDING_PX}px`,
        }}
      >
        <Box
          component="span"
          sx={{
            ...STEP_BADGE_PILL_SX,
            whiteSpace: "nowrap",
            alignSelf: "flex-start",
          }}
        >
          {stepLabel}
        </Box>

        {hasImages && (
          <Box sx={{ mt: 1.25 }}>
            <StepImageCollage
              items={images}
              onOpen={onOpenImage}
              tapToOpenLabel={tapToOpenLabel}
              morePhotosLabel={morePhotosLabel}
              zoomLabel={zoomLabel}
            />
          </Box>
        )}

        <StepInlineVideos videos={videos} compact />

        {heading ? (
          <Typography
            component="h3"
            sx={{
              ...ANSWER_HEADING_SX,
              mt: 1.25,
              mb: hasDescription ? 0.5 : 0,
            }}
          >
            {heading}
          </Typography>
        ) : null}

        {hasDescription && (
          <Box
            ref={containerRef}
            sx={{
              mt: heading ? 0 : 1.25,
              position: "relative",
              maxHeight: STEP_DESCRIPTION_MAX_HEIGHT_PX,
              overflow: "hidden",
              ...STEP_CONTENT_TYPOGRAPHY,
              "& p, & div, & li, & span": {
                m: 0,
                color: "inherit",
                fontSize: "inherit",
                fontWeight: "inherit",
                lineHeight: "inherit",
              },
              "& ul": { my: 0.5, pl: 2.25 },
              "& li": { mb: 0.35 },
            }}
          >
            <RagMarkdownContent text={previewMarkdown} dense />
            <Box
              ref={measureRef}
              aria-hidden
              sx={{
                position: "absolute",
                visibility: "hidden",
                pointerEvents: "none",
                top: 0,
                left: 0,
                height: "auto",
                maxHeight: "none",
                overflow: "visible",
                whiteSpace: "pre-wrap",
                ...STEP_CONTENT_TYPOGRAPHY,
              }}
            />
          </Box>
        )}

        {shouldShowMore && (
          <Typography
            component="button"
            type="button"
            onClick={() => setDialogOpen(true)}
            sx={{
              mt: 0.75,
              p: 0,
              border: "none",
              bgcolor: "transparent",
              cursor: "pointer",
              color: "primary.main",
              fontSize: 13,
              fontWeight: 600,
              lineHeight: 1.4,
              textAlign: "left",
              alignSelf: "flex-start",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {t("opva.chatAssistanceShowMore")}
          </Typography>
        )}

        {actionLink && (
          <Typography
            component="a"
            href={actionLink.url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: "inline-block",
              mt: 1,
              color: "primary.main",
              fontSize: 13,
              fontWeight: 600,
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {actionLink.label} ↗
          </Typography>
        )}
      </Box>

      <StepDescriptionDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        stepLabel={stepLabel}
        heading={heading}
        content={content}
        videos={videos}
        closeLabel={t("opva.chatAssistanceClose")}
      />
    </>
  );
}
